import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HiMinus, HiPlus, HiOutlineTrash } from 'react-icons/hi';
import EmptyProductLogo from '../../assets/images/emptyProductLogo.svg';
import { getNumberFormat } from '../../utils/getNumberFormat';
import {
  optionDisplayName,
  extendedOptionPrice,
} from '../../utils/optionQty';
import useBasketMenuIndex from '../../utils/hooks/useBasketMenuIndex';
import { basketOptionsInMenuOrder } from '../../utils/menuOrder';
import {
  decrementQty,
  incrementQty,
  removeItem,
} from '../../store/basket/basketSlice';
import useCouponValidation from '../../utils/hooks/useCouponValidation';
import {
  calculateBasketItemCouponPrices,
  isBXGYCoupon,
  isSpendGetFreeItemCoupon,
} from '../../utils/coupon';
import {
  getBuyItemCount,
  getGetItemCount,
} from '../../utils/coupon/types';
import ProductModal from './Product/ProductModal';

/**
 * Redesigned cart line item for the new UI.
 * - Compact square image + stacked variant/options/note.
 * - Refined qty stepper, right-aligned pricing with discount split.
 * - Tapping the image/name navigates to product detail for editing,
 *   matching the legacy behavior.
 */
const BasketProduct = ({ product }) => {
  const dispatch = useDispatch();
  const basket = useSelector((state) => state.basket.basket);
  const menuIndex = useBasketMenuIndex();
  const { basketId, options, qty } = product;
  const { appliedCoupon } = useCouponValidation();
  const [modalOpen, setModalOpen] = useState(false);

  const isFreeItem =
    isSpendGetFreeItemCoupon(appliedCoupon) &&
    appliedCoupon?.freeitemid === product.item.id &&
    product.isFreeItemFromCoupon === true;

  const isBXGYItem = isBXGYCoupon(appliedCoupon) && product.bxgyInfo;

  const couponPrices = calculateBasketItemCouponPrices({
    basketItem: product,
    appliedCoupon,
    basketItems: basket.items || [],
  });

  const itemPrice = product.item?.price || 0;
  const optionsTotal = (options || []).reduce(
    (sum, opt) => sum + (opt.price || 0) * (opt.qty ?? 1),
    0,
  );
  const normalTotal = (itemPrice + optionsTotal) * qty;

  const handleRemove = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    dispatch(removeItem(basketId));
  };
  const handleDec = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (qty === 1) return handleRemove();
    dispatch(decrementQty(basketId));
  };
  const handleInc = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    dispatch(incrementQty(basketId));
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setModalOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setModalOpen(true);
          }
        }}
        className="grid items-start gap-[12px] cursor-pointer"
        style={{
          gridTemplateColumns: '56px 1fr auto',
          padding: '14px 0',
          borderBottom: '1px solid var(--ui-line)',
        }}
      >
        {/* Image */}
        <div
          className="overflow-hidden"
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '10px',
            background: 'var(--ui-line)',
            flexShrink: 0,
          }}
        >
          <img
            alt={product.product.name}
            src={product.product.imagepath || EmptyProductLogo}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Info */}
        <div className="min-w-0">
          <h5
            className="truncate"
            style={{
              fontSize: '14px',
              fontWeight: 700,
              letterSpacing: '-0.015em',
              lineHeight: 1.3,
              color: 'var(--ui-ink)',
            }}
          >
            {product.product.name}
          </h5>

          {/* Coupon badges */}
          {isBXGYItem && <BxgyBadge qty={qty} coupon={appliedCoupon} />}
          {isFreeItem && (
            <FreeItemBadge
              minNotMet={couponPrices?.minpriceNotMet}
              minPrice={appliedCoupon?.minprice}
            />
          )}

          {/* Main variant */}
          <div
            style={{
              fontSize: '12px',
              color: 'var(--ui-ink-2)',
              marginTop: '3px',
              lineHeight: 1.4,
            }}
          >
            {product.item.name}
            {product.item.price > 0 && (
              <span
                className="ml-1"
                style={{ fontFamily: 'Geist Mono, monospace' }}
              >
                +${getNumberFormat(product.item.price)}
              </span>
            )}
          </div>

          {/* Options */}
          {options.length > 0 &&
            basketOptionsInMenuOrder(options, menuIndex).map((option, idx) => (
                <div
                  key={`${option.id}-${idx}-${product.basketId}-Option`}
                  style={{
                    fontSize: '12px',
                    color: 'var(--ui-ink-2)',
                    lineHeight: 1.4,
                  }}
                >
                  {optionDisplayName(option.name, option.qty ?? 1)}
                  {option.price > 0 && (
                    <span
                      className="ml-1"
                      style={{ fontFamily: 'Geist Mono, monospace' }}
                    >
                      +$
                      {getNumberFormat(
                        extendedOptionPrice(option.price, option.qty ?? 1),
                      )}
                    </span>
                  )}
                </div>
              ))}

          {/* Note */}
          {product.note !== '' && (
            <div
              style={{
                fontSize: '11.5px',
                color: 'var(--ui-ink-2)',
                fontStyle: 'italic',
                marginTop: '4px',
              }}
            >
              “{product.note}”
            </div>
          )}
        </div>

        {/* Right column: qty stepper + price */}
        <div
          className="flex flex-col items-end gap-2 flex-shrink-0"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div
            className="inline-flex items-center rounded-[10px] overflow-hidden"
            style={{
              background: 'var(--ui-surface-2)',
              border: '1px solid var(--ui-line)',
            }}
          >
            <button
              type="button"
              onClick={handleDec}
              className="grid place-items-center transition-colors"
              style={{
                width: '26px',
                height: '28px',
                background: 'transparent',
                border: 'none',
                color: qty === 1 ? '#DC2626' : 'var(--ui-ink)',
                cursor: 'pointer',
              }}
              aria-label={qty === 1 ? 'Remove' : 'Decrease'}
            >
              {qty === 1 ? (
                <HiOutlineTrash size={12} />
              ) : (
                <HiMinus size={12} />
              )}
            </button>
            <span
              className="font-bold text-center"
              style={{
                fontSize: '13px',
                minWidth: '20px',
                color: 'var(--ui-ink)',
              }}
            >
              {qty}
            </span>
            <button
              type="button"
              onClick={handleInc}
              className="grid place-items-center transition-colors"
              style={{
                width: '26px',
                height: '28px',
                background: 'transparent',
                border: 'none',
                color: 'var(--ui-ink)',
                cursor: 'pointer',
              }}
              aria-label="Increase"
            >
              <HiPlus size={12} />
            </button>
          </div>

          <div
            className="text-right"
            style={{
              fontFamily: 'Geist Mono, monospace',
              fontWeight: 700,
              minWidth: '60px',
            }}
          >
            {couponPrices && !couponPrices.minpriceNotMet ? (
              <div className="flex flex-col items-end leading-[1.1]">
                <span
                  style={{ color: 'var(--ui-green)', fontSize: '14px' }}
                >
                  ${getNumberFormat(couponPrices.displayTotal)}
                </span>
                <span
                  style={{
                    color: 'var(--ui-ink-3)',
                    textDecoration: 'line-through',
                    fontSize: '11px',
                  }}
                >
                  ${getNumberFormat(couponPrices.originalTotal)}
                </span>
              </div>
            ) : couponPrices?.minpriceNotMet ? (
              <span style={{ fontSize: '14px', color: 'var(--ui-ink)' }}>
                ${getNumberFormat(couponPrices.displayTotal)}
              </span>
            ) : (
              <span style={{ fontSize: '14px', color: 'var(--ui-ink)' }}>
                ${getNumberFormat(normalTotal)}
              </span>
            )}
          </div>
        </div>
      </div>

      {modalOpen && (
        <ProductModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          product={{ ...product.product, basketProduct: product }}
        />
      )}
    </>
  );
};

const BxgyBadge = ({ qty, coupon }) => {
  const BuyItemCount = getBuyItemCount(coupon);
  const GetItemCount = getGetItemCount(coupon);
  const minQtyForFree = BuyItemCount + 1;
  if (qty >= minQtyForFree) {
    return (
      <span
        className="inline-block mt-[3px]"
        style={{
          padding: '2px 8px',
          fontSize: '10.5px',
          fontWeight: 700,
          letterSpacing: '0.02em',
          borderRadius: '100px',
          background: 'rgba(21, 128, 61, 0.1)',
          color: 'var(--ui-green)',
        }}
      >
        Buy {BuyItemCount} · Get {GetItemCount} applied
      </span>
    );
  }
  return (
    <span
      className="inline-block mt-[3px]"
      style={{
        padding: '2px 8px',
        fontSize: '10.5px',
        fontWeight: 700,
        letterSpacing: '0.02em',
        borderRadius: '100px',
        background: 'rgba(220, 38, 38, 0.08)',
        color: '#B91C1C',
      }}
    >
      Add {minQtyForFree - qty} more to unlock
    </span>
  );
};

const FreeItemBadge = ({ minNotMet, minPrice }) =>
  minNotMet ? (
    <span
      className="inline-block mt-[3px]"
      style={{
        padding: '2px 8px',
        fontSize: '10.5px',
        fontWeight: 700,
        borderRadius: '100px',
        background: 'rgba(220, 38, 38, 0.08)',
        color: '#B91C1C',
      }}
    >
      Free at ${minPrice || 0}+
    </span>
  ) : (
    <span
      className="inline-block mt-[3px]"
      style={{
        padding: '2px 8px',
        fontSize: '10.5px',
        fontWeight: 700,
        borderRadius: '100px',
        background: 'rgba(21, 128, 61, 0.1)',
        color: 'var(--ui-green)',
      }}
    >
      Free item applied
    </span>
  );

export default BasketProduct;
